import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Logo from "../../public/AfricFreshLogo-01.svg";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import Alert from '../../components/alert-message';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';

const LoginForm = () => {
    // Login state
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [alertType, setAlertType] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    // Forgot password state
    const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
    const [otp, setOtp] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [otpSent, setOtpSent] = useState(false);
    const [otpExpiry, setOtpExpiry] = useState(null);
    const [resendTime, setResendTime] = useState(60);
    const [canResend, setCanResend] = useState(true);

    const navigate = useNavigate();

    // Redirect if token exists
    useEffect(() => {
        const token = localStorage.getItem('AficFreshtoken');
        if (token) {
            navigate('/');
        }
    }, [navigate]);

    // Handle OTP resend countdown
    useEffect(() => {
        let timer;
        if (otpSent && resendTime > 0) {
            timer = setTimeout(() => {
                setResendTime(prev => prev - 1);
            }, 1000);
        } else if (resendTime === 0) {
            setCanResend(true);
        }
        return () => clearTimeout(timer);
    }, [resendTime, otpSent]);

    const validate = () => {
        let tempErrors = {};
        
        if (forgotPasswordMode) {
            if (!otpSent) {
                const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{3,}$/;
                tempErrors.email = emailPattern.test(email) ? "" : "Email is not valid.";
            } else {
                tempErrors.otp = otp ? "" : "OTP is required.";
                tempErrors.newPassword = newPassword ? "" : "New password is required.";
                tempErrors.confirmPassword = confirmPassword === newPassword ? "" : "Passwords do not match.";
            }
        } else {
            const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{3,}$/;
            tempErrors.email = emailPattern.test(email) ? "" : "Email is not valid.";
            tempErrors.password = password ? "" : "Password is required.";
        }

        setErrors(tempErrors);
        return Object.values(tempErrors).every(x => x === "");
    };

    const handleLogin = async () => {
        try {
            const response = await axios.post(
                'https://afric-fresh-backend.onrender.com/auth/login',
                { email, password },
                { headers: { 'Content-Type': 'application/json' } }
            );

            const expiresAt = new Date(new Date().getTime() + 24 * 60 * 60 * 1000);
            localStorage.setItem('AficFreshtoken', response.data.token);
            localStorage.setItem('AficFreshExpiresAt', expiresAt.toISOString());
            localStorage.setItem('AficFreshuserDetails', JSON.stringify(response.data.user));

            setMessage(response.data.message);
            setAlertType('success');
            setTimeout(() => navigate('/'), 3000);
        } catch (error) {
            setMessage(error.response?.data?.error || 'Login failed');
            setAlertType('error');
            setTimeout(() => setMessage(""), 5000);
        } finally {
            setLoading(false);
        }
    };

    const handleSendOtp = async () => {
        if (!validate()) return;
        setLoading(true);

        try {
            const response = await axios.post(
                'https://afric-fresh-backend.onrender.com/auth/forgot-password',
                { email },
                { headers: { 'Content-Type': 'application/json' } }
            );

            setMessage('OTP sent to your email');
            setAlertType('success');
            setOtpSent(true);
            setOtpExpiry(new Date(new Date().getTime() + 5 * 60 * 1000)); // 5 minutes expiry
            setResendTime(60); // 1 minute resend timer
            setCanResend(false);
        } catch (error) {
            setMessage(error.response?.data?.error || 'Failed to send OTP');
            setAlertType('error');
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async () => {
        if (!validate()) return;
        setLoading(true);

        try {
            const response = await axios.post(
                'https://afric-fresh-backend.onrender.com/auth/reset-password',
                { email, otp, newPassword },
                { headers: { 'Content-Type': 'application/json' } }
            );

            setMessage('Password reset successfully. You can now login with your new password.');
            setAlertType('success');
            setTimeout(() => {
                setForgotPasswordMode(false);
                setOtpSent(false);
                setOtp('');
                setNewPassword('');
                setConfirmPassword('');
            }, 3000);
        } catch (error) {
            setMessage(error.response?.data?.error || 'Failed to reset password');
            setAlertType('error');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setLoading(true);

        if (forgotPasswordMode) {
            if (!otpSent) {
                await handleSendOtp();
            } else {
                await handleResetPassword();
            }
        } else {
            await handleLogin();
        }
    };

    const handleResendOtp = async () => {
        if (!canResend) return;
        setLoading(true);

        try {
            const response = await axios.post(
                'https://afric-fresh-backend.onrender.com/auth/resend-otp',
                { email },
                { headers: { 'Content-Type': 'application/json' } }
            );

            setMessage('New OTP sent to your email');
            setAlertType('success');
            setResendTime(60);
            setCanResend(false);
        } catch (error) {
            setMessage(error.response?.data?.error || 'Failed to resend OTP');
            setAlertType('error');
        } finally {
            setLoading(false);
        }
    };

    // Clear errors after 5 seconds
    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            const errorTimeout = setTimeout(() => setErrors({}), 5000);
            return () => clearTimeout(errorTimeout);
        }
    }, [errors]);

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: { 
            opacity: 1,
            transition: { 
                duration: 0.5,
                when: "beforeChildren",
                staggerChildren: 0.1
            }
        },
        exit: {
            opacity: 0,
            transition: { duration: 0.3 }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: { 
            y: 0, 
            opacity: 1,
            transition: {
                type: "spring", 
                stiffness: 300,
                damping: 24
            }
        }
    };

    const formSwitchVariants = {
        initial: { opacity: 0, y: 20 },
        animate: { 
            opacity: 1, 
            y: 0,
            transition: {
                duration: 0.4,
                ease: "easeOut"
            }
        },
        exit: { 
            opacity: 0, 
            y: -20,
            transition: {
                duration: 0.3
            }
        }
    };

    return (
        <motion.div 
            className='flex min-h-screen bg-gray-100 max-sm:bg-white'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
        >
            <Helmet>
                <title>Login | AfricFresh - African Grocery Marketplace</title>
                <meta 
                    name="description" 
                    content="Login to your AfricFresh account to shop for authentic African groceries, fresh produce, and food products. Access your order history and saved preferences."
                />
                <meta property="og:title" content="Login | AfricFresh - African Grocery Marketplace" />
                <meta property="og:description" content="Access your AfricFresh account to shop for African food products and groceries." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://africfresh.onrender.com/login" />
                <meta property="og:image" content={Logo} />
                <meta name="keywords" content="African groceries login, AfricFresh account, African food marketplace, online African store, African produce login" />
            </Helmet>
            <div className='flex justify-center items-center flex-1'>
                <motion.div 
                    className="flex-row flex-col"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                >
                    <motion.div 
                        className="flex justify-center items-center"
                        variants={itemVariants}
                    >
                        <motion.img 
                            src={Logo} 
                            alt="AfriFresh-Logo" 
                            className="w-72 h-20 mb-2" 
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ 
                                duration: 0.5,
                                type: "spring",
                                stiffness: 260,
                                damping: 20
                            }}
                        />
                    </motion.div>
                    <motion.div 
                        className='bg-white p-3 rounded-lg shadow-lg max-sm:rounded-none max-sm:shadow-none max-w-sm w-full text-center h-full max-sm:justify-between'
                        variants={itemVariants}
                        initial={{ y: 30, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.2, duration: 0.5 }}
                    >
                        <motion.h2 
                            className="text-2xl font-bold mb-3"
                            variants={itemVariants}
                        >
                            {forgotPasswordMode ? 'Reset Password' : 'Login to your account'}
                        </motion.h2>
                        <motion.p 
                            className="text-gray-600 mb-4"
                            variants={itemVariants}
                        >
                            {forgotPasswordMode 
                                ? (otpSent 
                                    ? 'Enter the OTP sent to your email and your new password' 
                                    : 'Enter your email to receive a password reset OTP')
                                : 'Welcome back to AfricFresh! Log in to access your account and start shopping for your favorite African groceries.'}
                        </motion.p>
                       
                        <AnimatePresence>
                            {message && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <Alert type={alertType} message={message} onClose={() => setMessage('')} />
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <AnimatePresence mode="wait">
                            <motion.div
                                key={`${forgotPasswordMode}-${otpSent}`}
                                variants={formSwitchVariants}
                                initial="initial"
                                animate="animate"
                                exit="exit"
                            >
                                <form onSubmit={handleSubmit} noValidate>
                                    {/* Email field (always shown) */}
                                    <motion.div 
                                        className="w-full mb-3"
                                        variants={itemVariants}
                                    >
                                        <input
                                            type="email"
                                            placeholder="Email"
                                            className="w-full p-2 border-b focus:border-b outline-none border-gray-300 rounded-lg transition-all duration-300 focus:border-[#248526]"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                        />
                                        <AnimatePresence>
                                            {errors.email && (
                                                <motion.p 
                                                    className="text-red-500 text-xs mt-1 text-left"
                                                    initial={{ opacity: 0, y: -5 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, y: -5 }}
                                                    transition={{ duration: 0.2 }}
                                                >
                                                    {errors.email}
                                                </motion.p>
                                            )}
                                        </AnimatePresence>
                                    </motion.div>

                                    {/* OTP field (shown in forgot password mode after OTP sent) */}
                                    {forgotPasswordMode && otpSent && (
                                        <motion.div 
                                            className="w-full mb-3"
                                            variants={itemVariants}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            <input
                                                type="text"
                                                placeholder="Enter OTP"
                                                className="w-full p-2 border-b focus:border-b outline-none border-gray-300 rounded-lg transition-all duration-300 focus:border-[#248526]"
                                                value={otp}
                                                onChange={(e) => setOtp(e.target.value)}
                                            />
                                            <AnimatePresence>
                                                {errors.otp && (
                                                    <motion.p 
                                                        className="text-red-500 text-xs mt-1 text-left"
                                                        initial={{ opacity: 0, y: -5 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        exit={{ opacity: 0, y: -5 }}
                                                        transition={{ duration: 0.2 }}
                                                    >
                                                        {errors.otp}
                                                    </motion.p>
                                                )}
                                            </AnimatePresence>
                                            <div className="text-right mt-1">
                                                {canResend ? (
                                                    <motion.button 
                                                        type="button" 
                                                        className="text-[#154F16] text-xs hover:underline"
                                                        onClick={handleResendOtp}
                                                        whileHover={{ scale: 1.05 }}
                                                        whileTap={{ scale: 0.95 }}
                                                    >
                                                        Resend OTP
                                                    </motion.button>
                                                ) : (
                                                    <motion.span 
                                                        className="text-gray-500 text-xs"
                                                        initial={{ opacity: 0 }}
                                                        animate={{ opacity: 1 }}
                                                    >
                                                        Resend in {resendTime}s
                                                    </motion.span>
                                                )}
                                            </div>
                                        </motion.div>
                                    )}

                                    {/* New password fields (shown in forgot password mode after OTP sent) */}
                                    {forgotPasswordMode && otpSent && (
                                        <>
                                            <motion.div 
                                                className="w-full mb-3 relative"
                                                variants={itemVariants}
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.3, delay: 0.1 }}
                                            >
                                                <input
                                                    type={showNewPassword ? "text" : "password"}
                                                    placeholder="New Password"
                                                    className="w-full p-2 border-b focus:border-b outline-none border-gray-300 rounded-lg pr-10 transition-all duration-300 focus:border-[#248526]"
                                                    value={newPassword}
                                                    onChange={(e) => setNewPassword(e.target.value)}
                                                />
                                                <motion.button
                                                    type="button"
                                                    className="absolute right-2 top-2 text-gray-500"
                                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                                    whileHover={{ scale: 1.1 }}
                                                    whileTap={{ scale: 0.9 }}
                                                >
                                                    <FontAwesomeIcon icon={showNewPassword ? faEyeSlash : faEye} />
                                                </motion.button>
                                                <AnimatePresence>
                                                    {errors.newPassword && (
                                                        <motion.p 
                                                            className="text-red-500 text-xs mt-1 text-left"
                                                            initial={{ opacity: 0, y: -5 }}
                                                            animate={{ opacity: 1, y: 0 }}
                                                            exit={{ opacity: 0, y: -5 }}
                                                            transition={{ duration: 0.2 }}
                                                        >
                                                            {errors.newPassword}
                                                        </motion.p>
                                                    )}
                                                </AnimatePresence>
                                            </motion.div>
                                            <motion.div 
                                                className="w-full mb-3 relative"
                                                variants={itemVariants}
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.3, delay: 0.2 }}
                                            >
                                                <input
                                                    type={showConfirmPassword ? "text" : "password"}
                                                    placeholder="Confirm New Password"
                                                    className="w-full p-2 border-b focus:border-b outline-none border-gray-300 rounded-lg pr-10 transition-all duration-300 focus:border-[#248526]"
                                                    value={confirmPassword}
                                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                                />
                                                <motion.button
                                                    type="button"
                                                    className="absolute right-2 top-2 text-gray-500"
                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                    whileHover={{ scale: 1.1 }}
                                                    whileTap={{ scale: 0.9 }}
                                                >
                                                    <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                                                </motion.button>
                                                <AnimatePresence>
                                                    {errors.confirmPassword && (
                                                        <motion.p 
                                                            className="text-red-500 text-xs mt-1 text-left"
                                                            initial={{ opacity: 0, y: -5 }}
                                                            animate={{ opacity: 1, y: 0 }}
                                                            exit={{ opacity: 0, y: -5 }}
                                                            transition={{ duration: 0.2 }}
                                                        >
                                                            {errors.confirmPassword}
                                                        </motion.p>
                                                    )}
                                                </AnimatePresence>
                                            </motion.div>
                                        </>
                                    )}

                                    {/* Password field (shown in login mode) */}
                                    {!forgotPasswordMode && (
                                        <motion.div 
                                            className="w-full mb-5 relative"
                                            variants={itemVariants}
                                        >
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                placeholder="Password"
                                                className="w-full p-2 border-b focus:border-b outline-none border-gray-300 rounded-lg pr-10 transition-all duration-300 focus:border-[#248526]"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                            />
                                            <motion.button
                                                type="button"
                                                className="absolute right-2 top-2 text-gray-500"
                                                onClick={() => setShowPassword(!showPassword)}
                                                whileHover={{ scale: 1.1 }}
                                                whileTap={{ scale: 0.9 }}
                                            >
                                                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                                            </motion.button>
                                            <AnimatePresence>
                                                {errors.password && (
                                                    <motion.p 
                                                        className="text-red-500 text-xs mt-1 text-left"
                                                        initial={{ opacity: 0, y: -5 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        exit={{ opacity: 0, y: -5 }}
                                                        transition={{ duration: 0.2 }}
                                                    >
                                                        {errors.password}
                                                    </motion.p>
                                                )}
                                            </AnimatePresence>
                                        </motion.div>
                                    )}

                                    <motion.div 
                                        className="mt-auto"
                                        variants={itemVariants}
                                    >
                                        <motion.button
                                            type="submit"
                                            className="bg-[#248526] text-white py-2 w-full rounded-lg hover:bg-[#144b15] flex justify-center items-center"
                                            disabled={loading}
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            transition={{ type: "spring", stiffness: 400, damping: 17 }}
                                        >
                                            {loading ? (
                                                <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                                            ) : (
                                                forgotPasswordMode 
                                                    ? (otpSent ? "Reset Password" : "Send OTP") 
                                                    : "Login"
                                            )}
                                        </motion.button>
                                    </motion.div>
                                </form>
                            </motion.div>
                        </AnimatePresence>

                        {!forgotPasswordMode ? (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.4, duration: 0.3 }}
                            >
                                <motion.button 
                                    onClick={() => setForgotPasswordMode(true)}
                                    className="text-[#154F16] mt-3 font-bold underline mb-4 block"
                                    whileHover={{ scale: 1.03 }}
                                    whileTap={{ scale: 0.97 }}
                                >
                                    Forgot password?
                                </motion.button>
                                <motion.div
                                    whileHover={{ scale: 1.03 }}
                                    whileTap={{ scale: 0.97 }}
                                >
                                    <Link to="/auth/signup" className="text-[#154F16] font-bold underline mb-4 block">
                                        Don't have an account yet? Sign up here!
                                    </Link>
                                </motion.div>
                            </motion.div>
                        ) : (
                            <motion.button 
                                onClick={() => {
                                    setForgotPasswordMode(false);
                                    setOtpSent(false);
                                    setOtp('');
                                    setNewPassword('');
                                    setConfirmPassword('');
                                }}
                                className="text-[#154F16] mt-3 font-bold underline mb-4 block"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.4, duration: 0.3 }}
                                whileHover={{ scale: 1.03 }}
                                whileTap={{ scale: 0.97 }}
                            >
                                Back to login
                            </motion.button>
                        )}

                        <motion.p 
                            className="text-xs text-gray-500 mt-4"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.5, duration: 0.3 }}
                        >
                            By {forgotPasswordMode ? 'resetting your password' : 'logging in'}, I agree to the AfricFresh{' '}
                            <a href="#" className="text-[#154F16] hover:underline">
                                Terms & Conditions
                            </a>{' '}
                            and acknowledge the{' '}
                            <a href="#" className="text-[#154F16] hover:underline">
                                Privacy Policy
                            </a>.
                        </motion.p>
                    </motion.div>
                </motion.div>
            </div>
        </motion.div>
    );
};

export default LoginForm;